import type { PixelCanvas } from '../../../lib';
import { sprite } from '../../../lib';
import { burst, frames3, frames4, muzzle, paint, shot } from './shared';

/**
 * The Pepper Mill's shots (the Chef set's gun, ADR-0160 amended): a red chilli pepper on fire (a green stalk at its back,
 * its tip at the pellet's head, flames licking off it), a puff of ground pepper at the muzzle, a pop of spice and embers
 * where it lands; and the flourish's kitchenware (`chef_bit`: a pot, a pan, a ladle) and the toque collapsing
 * (`chef_toque`).
 */

/** A chilli, its tip at (24, 8): red, lit along its top, curling down at its tip; a flame licking back off it. */
const chilli = (f: number) => (c: PixelCanvas) =>
  paint(c, (x, y) => {
    // The pepper: thickest by its stalk, narrowing to a tip bent a little down.
    if (x > 11 && x < 25) {
      const t = (x - 12) / 13;
      const mid = 7.6 + t * t * 1.6;
      const half = 2.6 * (1 - t * t) + 0.5;
      const d = y - mid;
      if (Math.abs(d) < half) return d < -half + 1 ? 'i' : d > half - 1 ? 'R' : 'r';
    }
    // The calyx and stalk.
    if (x > 9.5 && x <= 12 && Math.abs(y - 7.6) < 2.6) return x > 11 ? 'g' : 'G';
    if (x > 7 && x <= 9.5 && Math.abs(y - 7 + (9.5 - x) * 0.35) < 0.6) return 'l';
    // The flames licking back off it, flickering.
    if (x < 11 && x > 1) {
      const lick = Math.sin(x * 0.8 + f * Math.PI * 0.5) * (1.2 + (f % 2) * 0.8);
      const h = (x - 1) * 0.32;
      const d = Math.abs(y - 8 - lick * ((11 - x) / 10));
      if (d < h * 0.45) return 'y';
      if (d < h * 0.8) return 'o';
      if (d < h) return 'u';
    }
    return null;
  });

/** Lumps of a puff of ground pepper rolling out of the muzzle, by frame: [x, y, r]. */
const PUFFS = [
  [],
  [
    [11, 20, 4.2],
    [16, 17.5, 3],
    [16, 22.5, 3],
  ],
  [
    [14, 20.5, 4],
    [20, 17, 4.2],
    [21, 23.5, 3.6],
    [26, 20, 3],
  ],
] as const;

/** A flash, then a puff of ground pepper (rust and orange, flecked black) rolling out of the muzzle and browning. */
const grind = (f: number) => (c: PixelCanvas) =>
  paint(c, (x, y) => {
    const d = Math.hypot(x - 7, y - 20);
    if (f === 0) return d < 2.5 ? 'w' : d < 5 ? 'y' : d < 7 ? 'o' : d < 8 ? 'r' : null;
    let near = Infinity;
    for (const [px, py, r] of PUFFS[f] as readonly (readonly [number, number, number])[])
      near = Math.min(near, Math.hypot(x - px, y - py) / r);
    if (near >= 1) return null;
    if ((Math.floor(x) * 7 + Math.floor(y) * 3 + f) % 13 === 0) return 'k';
    if (f === 1) return near < 0.45 ? 'o' : near < 0.8 ? 'U' : 'u';
    return near < 0.5 ? 'u' : near < 0.85 ? 'U' : 'n';
  });

/** A pop of spice: a flash, then a puff of red and rust with embers flung out of it, thinning and drifting up. */
const pop = (f: number) => (c: PixelCanvas) =>
  paint(c, (x, y) => {
    const [dx, dy] = [x - 22, y - 22];
    const d = Math.hypot(dx, dy);
    if (f === 0) return d < 3 ? 'w' : d < 6 ? 'y' : d < 8 ? 'o' : null;
    const R = ([0, 8, 10, 11] as const)[f] as number;
    const lift = f * 1.5;
    const dl = Math.hypot(dx, dy + lift);
    const lumps = R * (0.8 + 0.2 * Math.sin(Math.atan2(dy, dx) * 5 + f * 2));
    if (dl < lumps && (f < 3 || (Math.floor(x) + Math.floor(y)) % 2 === 0)) {
      if (f === 1 && dl < 3.5) return 'y';
      if (dl < lumps * 0.5) return f === 1 ? 'o' : 'r';
      return dx + dy < -R * 0.4 ? 'U' : f === 3 ? 'n' : 'u';
    }
    // Embers flung out.
    if (f < 3)
      for (let i = 0; i < 8; i++) {
        const a = (i / 8) * Math.PI * 2 + 0.4;
        const r = R + 2 + f * 4;
        if (Math.hypot(x - 22 - Math.cos(a) * r, y - 22 - Math.sin(a) * r + f) < 0.9) return f === 1 ? 'y' : 'o';
      }
    return null;
  });

/** A pot, a frying pan or a ladle (16 × 16, middle (8, 8)), each a frame of its own. */
const ware = (f: number) => (c: PixelCanvas) =>
  paint(c, (x, y) => {
    if (f === 0) {
      // A steel pot: its lid and knob, two handles, its body lit on the left.
      if (Math.abs(x - 8) < 1.2 && y > 3 && y < 5) return '2';
      if (y > 5 && y < 7 && Math.abs(x - 8) < 6) return y < 6 ? '5' : '4';
      if (y >= 7 && y < 13 && Math.abs(x - 8) < 5.5) return x < 5 ? '5' : x > 10 ? '3' : '4';
      if (y >= 7.5 && y < 9 && Math.abs(x - 8) < 7.5) return '2';
      return null;
    }
    if (f === 1) {
      // A black frying pan: its round face, a long handle.
      const d = Math.hypot((x - 6) / 5.5, (y - 9) / 4.5);
      if (d < 1) return d > 0.75 ? (x < 6 ? '3' : '1') : x + y < 13 ? '2' : '1';
      if (x > 10.5 && x < 15.5 && Math.abs(y - 8.6 + (x - 10.5) * 0.6) < 0.9) return x < 12 ? '2' : 'n';
      return null;
    }
    // A ladle: a silver bowl, a long handle with a hook.
    const d = Math.hypot((x - 5) / 3.5, (y - 11) / 3);
    if (d < 1 && y > 10) return x < 5 ? '5' : '4';
    if (Math.abs(y - (11 - (x - 7) * 0.9)) < 0.8 && x > 6.5 && x < 14) return x < 9 ? '5' : '4';
    if (x > 12.5 && x < 14.5 && y > 3 && y < 5) return '3';
    return null;
  });

/** The toque falling (20 × 24, its foot at (10, 22)): upright, slumping, folding over, flat. */
const toque = (f: number) => (c: PixelCanvas) =>
  paint(c, (x, y) => {
    const squash = ([1, 0.75, 0.5, 0.25] as const)[f] as number;
    const lean = ([0, 1.5, 3.5, 5] as const)[f] as number;
    const foot = 22;
    const h = 18 * squash;
    // The band at its foot in red (white is lost on lawn; the team's keys would need the team's flourish).
    if (y > foot - 3 && y <= foot && Math.abs(x - 10) < 6) return y < foot - 1.5 ? 'r' : 'R';
    const t = (foot - 3 - y) / h;
    if (t < 0 || t > 1) return null;
    const cx = 10 + lean * t * t;
    const w = 5 + t * 1.5 + (t > 0.6 ? Math.sin(((t - 0.6) / 0.4) * Math.PI) * 3 : 0);
    if (Math.abs(x - cx) > w) return null;
    if (t > 0.6 && t < 0.7 && Math.abs(x - cx) > 3) return '4';
    if (t <= 0.6 && Math.floor(x - cx + 20) % 3 === 0) return '5';
    return x < cx ? 'w' : x - cx > w - 2 ? '4' : '5';
  });

export default [
  sprite({
    name: 'pepper_shot',
    ...shot,
    notes: `The Pepper Mill's bullet (ADR-0160 amended): a red chilli pepper (lit pink along its top, dark red under it,
its tip bent a little down at the pellet's head), a green calyx and stalk at its back, flames licking back off it
(yellow in their heart, orange, rust) flickering over 4 frames. Turned along its flight. Rubric (4×): S4 R5 P4 L4 C4 A4 F4.`,
    anims: { idle: { fps: 14, frames: frames4(chilli) } },
  }),
  sprite({
    name: 'pepper_muzzle',
    ...muzzle,
    notes: `The Pepper Mill's muzzle flash (ADR-0160 amended): a white, gold and red flash, then a puff of ground pepper
(rust and orange, flecked black) rolling out of the muzzle and browning. Points right. Rubric (4×): S4 R4 P4 L4 C4 A4 F4.`,
    anims: { flash: { fps: 30, loop: false, frames: frames3(grind) } },
  }),
  sprite({
    name: 'pepper_burst',
    ...burst,
    notes: `The Pepper Mill's hit (ADR-0160 amended): the chilli pops, a white and gold flash, then a lumpy puff of spice
(a red and orange heart, rust round it) with embers flung out, rising and thinning into a broken brown haze. 4 frames.
Rubric (4×): S4 R4 P4 L4 C4 A4 F4.`,
    anims: { burst: { fps: 16, loop: false, frames: frames4(pop) } },
  }),
  sprite({
    name: 'chef_bit',
    group: 'fx',
    size: [16, 16],
    origin: [8, 8],
    density: 4,
    notes: `The Chef's knock-out kitchenware (ADR-0160 amended), a frame each (the flourish picks one per piece): a steel
pot with its lid, knob and handles; a black frying pan with a brown handle; a silver ladle. Rubric (4×): S4 R4 P4 L4 C4 F4.`,
    anims: { idle: { fps: 1, frames: frames3(ware) } },
  }),
  sprite({
    name: 'chef_toque',
    group: 'fx',
    size: [20, 24],
    origin: [10, 22],
    density: 4,
    notes: `The Chef's knock-out toque (ADR-0160 amended): the tall pleated toque on its red band, its puffed crown
slumping, folding over and lying flat over 4 frames. Rubric (4×): S4 R4 P4 L4 C4 A4 F4.`,
    anims: { idle: { fps: 6, loop: false, frames: frames4(toque) } },
  }),
];
